import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import {
  getDisabledLearnerRouteFallbackPath,
  isLearnerRouteEnabled,
} from '@app/routes/learner-feature-availability'
import { learnerRoutes } from '@app/routes/learner-route-registry'
import { LegacyDashboardRedirect } from '@app/routes/legacy-dashboard-redirects'
import { learnerPaths, publicLegalPaths } from '@app/routes/route-paths'
import {
  getProtectedAuthRouteDecision,
  getPublicAuthRouteDecision,
  useAuthSession,
} from '@features/auth'
import { DashboardBootstrapProvider } from '@/legacy-compat/pages/dashboard/DashboardBootstrapProvider'
import { DashboardStateProvider } from '@features/dashboard'
import { AppRouteErrorState, AppRouteLoadingState } from '@shared/ui'
import { useTranslation } from 'react-i18next'

const LandingPage = lazy(() =>
  import('@features/auth/routes').then((module) => ({
    default: module.LandingPage,
  })),
)

const LoginPage = lazy(() =>
  import('@features/auth/routes').then((module) => ({
    default: module.LoginPage,
  })),
)

const RegisterPage = lazy(() =>
  import('@features/auth/routes').then((module) => ({
    default: module.RegisterPage,
  })),
)

const GoogleOnboardingPage = lazy(() =>
  import('@features/auth/routes').then((module) => ({
    default: module.GoogleOnboardingPage,
  })),
)

const PublicLegalIndexPage = lazy(() =>
  import('@features/legal/routes').then((module) => ({
    default: module.PublicLegalIndexPage,
  })),
)

const LegalTermsPage = lazy(() =>
  import('@features/legal/routes').then((module) => ({
    default: () => <module.LegalDocumentPage documentId="terms" />,
  })),
)

const LegalPrivacyPage = lazy(() =>
  import('@features/legal/routes').then((module) => ({
    default: () => <module.LegalDocumentPage documentId="privacy" />,
  })),
)

const LegalCookiesPage = lazy(() =>
  import('@features/legal/routes').then((module) => ({
    default: () => <module.LegalDocumentPage documentId="cookies" />,
  })),
)

const AuthBrandPage = lazy(() =>
  import('@features/auth/routes').then((module) => ({
    default: module.AuthBrandPage,
  })),
)

const DesktopDownloadPage = lazy(() =>
  import('@features/desktop-download').then((module) => ({
    default: module.DesktopDownloadPage,
  })),
)

const AffiliateCenterPage = lazy(() =>
  import('@features/affiliate/routes').then((module) => ({
    default: module.AffiliateCenterPage,
  })),
)

const ReferralLandingPage = lazy(() =>
  import('@features/affiliate/routes').then((module) => ({
    default: module.ReferralLandingPage,
  })),
)

const LegacyCompatShell = lazy(() =>
  import('@/legacy-compat').then((module) => ({
    default: module.LegacyCompatShell,
  })),
)

const DevFlipbookLifecycleQaHarness = import.meta.env.DEV
  ? lazy(() => import('@features/learning/books/flipbook/FlipbookLifecycleQaHarness').then((module) => ({
      default: module.FlipbookLifecycleQaHarness,
    })))
  : null

const shortVideosFallbackPath = getDisabledLearnerRouteFallbackPath('short-videos')

function PublicOnlyRoute({ children }: { readonly children: ReactNode }) {
  const { isAuthenticated, sessionState } = useAuthSession()
  const routeDecision = getPublicAuthRouteDecision({
    authenticatedRedirectPath: learnerPaths.default,
    isAuthenticated,
    sessionState,
  })

  if (routeDecision.kind === 'loading') {
    return <AppRouteLoadingState message={routeDecision.loadingMessage ?? 'Checking session...'} />
  }

  if (routeDecision.kind === 'error') {
    return <AppRouteErrorState message={routeDecision.errorMessage ?? 'Unable to verify session.'} />
  }

  if (routeDecision.kind === 'redirect' && routeDecision.to) {
    return <Navigate replace to={routeDecision.to} />
  }

  return children
}

function GoogleOnboardingRoute({ children }: { readonly children: ReactNode }) {
  const { isAuthenticated } = useAuthSession()

  if (isAuthenticated) {
    return <Navigate replace to={learnerPaths.default} />
  }

  return children
}

function ProtectedLearnerRoute({
  children,
}: {
  readonly children: ReactNode
}) {
  const location = useLocation()
  const { isAuthenticated, sessionState, user } = useAuthSession()
  const routeDecision = getProtectedAuthRouteDecision({
    fromPathname: location.pathname,
    isAuthenticated,
    sessionState,
    userEmail: user?.email,
  })

  if (routeDecision.kind === 'loading') {
    return <AppRouteLoadingState message={routeDecision.loadingMessage ?? 'Checking session...'} />
  }

  if (routeDecision.kind === 'error') {
    return <AppRouteErrorState message={routeDecision.errorMessage ?? 'Unable to verify session.'} />
  }

  if (routeDecision.kind === 'redirect' && routeDecision.to) {
    return (
      <Navigate
        replace
        state={{
          from: routeDecision.from,
          reason: routeDecision.reason,
        }}
        to={routeDecision.to}
      />
    )
  }

  return children
}

function LearnerRoutesLayout() {
  const { i18n } = useTranslation('translation')
  const location = useLocation()
  const requiresLearningBootstrap =
    location.pathname === learnerPaths.learning ||
    location.pathname.startsWith(`${learnerPaths.learning}/`)

  return (
    <ProtectedLearnerRoute>
      <DashboardStateProvider>
        <DashboardBootstrapProvider
          i18n={i18n}
          requiresLearningBootstrap={requiresLearningBootstrap}
        >
          <Suspense fallback={<AppRouteLoadingState message="Loading learner dashboard..." />}>
            <Outlet />
          </Suspense>
        </DashboardBootstrapProvider>
      </DashboardStateProvider>
    </ProtectedLearnerRoute>
  )
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={<Suspense fallback={<AppRouteLoadingState message="Opening referral link..." />}><ReferralLandingPage /></Suspense>}
          path="/r/:code"
        />
        <Route
          element={
            <Suspense fallback={<AppRouteLoadingState message="Loading legal overview..." />}>
              <PublicLegalIndexPage />
            </Suspense>
          }
          path={publicLegalPaths.index}
        />
        <Route
          element={
            <Suspense fallback={<AppRouteLoadingState message="Loading terms and conditions..." />}>
              <LegalTermsPage />
            </Suspense>
          }
          path={publicLegalPaths.terms}
        />
        <Route
          element={
            <Suspense fallback={<AppRouteLoadingState message="Loading privacy policy..." />}>
              <LegalPrivacyPage />
            </Suspense>
          }
          path={publicLegalPaths.privacy}
        />
        <Route
          element={
            <Suspense fallback={<AppRouteLoadingState message="Loading cookie policy..." />}>
              <LegalCookiesPage />
            </Suspense>
          }
          path={publicLegalPaths.cookies}
        />
        <Route
          element={
            <PublicOnlyRoute>
              <Suspense fallback={<AppRouteLoadingState message="Loading landing page..." />}>
                <LandingPage />
              </Suspense>
            </PublicOnlyRoute>
          }
          path="/"
        />
        <Route
          element={
            <PublicOnlyRoute>
              <Suspense fallback={<AppRouteLoadingState message="Loading sign-in..." />}>
                <LoginPage />
              </Suspense>
            </PublicOnlyRoute>
          }
          path="/login"
        />
        <Route
          element={
            <PublicOnlyRoute>
              <Suspense fallback={<AppRouteLoadingState message="Loading registration..." />}>
                <RegisterPage />
              </Suspense>
            </PublicOnlyRoute>
          }
          path="/register"
        />
        <Route
          element={
            <GoogleOnboardingRoute>
              <Suspense fallback={<AppRouteLoadingState message="Loading Google account setup..." />}>
                <GoogleOnboardingPage />
              </Suspense>
            </GoogleOnboardingRoute>
          }
          path={learnerPaths.googleOnboarding}
        />
        <Route
          element={
            <GoogleOnboardingRoute>
              <Suspense fallback={<AppRouteLoadingState message="Loading account setup..." />}>
                <GoogleOnboardingPage mode="password" />
              </Suspense>
            </GoogleOnboardingRoute>
          }
          path={learnerPaths.passwordOnboarding}
        />
        <Route
          element={
            <Suspense fallback={<AppRouteLoadingState message="Loading brand review..." />}>
              <AuthBrandPage />
            </Suspense>
          }
          path="/brand"
        />
        <Route
          element={
            <Suspense fallback={<AppRouteLoadingState message="Loading desktop downloads..." />}>
              <DesktopDownloadPage />
            </Suspense>
          }
          path={learnerPaths.desktopDownload}
        />
        <Route element={<LearnerRoutesLayout />}>
          <Route element={<AffiliateCenterPage />} path={learnerPaths.affiliate} />
          {learnerRoutes.map((route) => (
            <Route
              element={route.element}
              key={route.id}
              path={route.path}
            />
          ))}
          {!isLearnerRouteEnabled('short-videos') && shortVideosFallbackPath ? (
            <Route
              element={<Navigate replace to={shortVideosFallbackPath} />}
              path={learnerPaths.shortVideos}
            />
          ) : null}
          {DevFlipbookLifecycleQaHarness ? (
            <Route
              element={
                <Suspense fallback={<AppRouteLoadingState message="Loading Flipbook QA harness..." />}>
                  <DevFlipbookLifecycleQaHarness />
                </Suspense>
              }
              path="/__qa/flipbook-annotation-lifecycle"
            />
          ) : null}
        </Route>
        <Route
          element={
            <ProtectedLearnerRoute>
              <LegacyDashboardRedirect />
            </ProtectedLearnerRoute>
          }
          path={learnerPaths.legacyDashboard}
        />
        <Route
          element={
            <ProtectedLearnerRoute>
              <Suspense fallback={<AppRouteLoadingState message="Loading legacy compatibility shell..." />}>
                <LegacyCompatShell />
              </Suspense>
            </ProtectedLearnerRoute>
          }
          path={learnerPaths.legacyCompat}
        />
        <Route element={<Navigate replace to="/" />} path="*" />
      </Routes>
    </BrowserRouter>
  )
}
